import { $apiAllo } from '@/services/api-allo/client';
import { $apiAlloRemote } from '@/services/api-allo/client-remote';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Contact, QueueNotReachedReason, QueueRoutingResult, SessionContact } from '../types';

type Result = {
  contacts: SessionContact[];
  isLoading: boolean;
};

type QueueNumberResponse = {
  number_to?: string;
  numberTo?: string;
  position?: number;
  call_id?: string | null;
  callId?: string | null;
  call_start_date?: string | null;
  callStartDate?: string | null;
  called_at?: string | null;
  calledAt?: string | null;
  routing_result?: QueueRoutingResult | null;
  routingResult?: QueueRoutingResult | null;
  not_reached_destination_reason?: QueueNotReachedReason | null;
  notReachedDestinationReason?: QueueNotReachedReason | null;
  detected_outbound_voicemail?: boolean | null;
  detectedOutboundVoicemail?: boolean | null;
  voicemail_left?: boolean | null;
  voicemailLeft?: boolean | null;
};

type NormalizedQueueNumber = {
  queueItemId: string;
  numberTo: string;
  position?: number;
  callId?: string;
  callStartDate?: string;
  routingResult?: QueueRoutingResult;
  notReachedReason?: QueueNotReachedReason;
  detectedOutboundVoicemail?: boolean;
  voicemailLeft?: boolean;
};

type DialingQueueResponse = {
  id?: string;
  numbers?: QueueNumberResponse[];
};

/** Shape returned by `GET /v1/contact?filter[number]=...`. */
type ContactLookupResponse = {
  data?: Contact[];
};

const getQueueNumber = (number: QueueNumberResponse): string | undefined =>
  number.number_to ?? number.numberTo;

const getQueueCallId = (number: QueueNumberResponse): string | undefined =>
  number.call_id ?? number.callId ?? undefined;

const getQueueCallStartDate = (number: QueueNumberResponse): string | undefined =>
  number.call_start_date ??
  number.callStartDate ??
  number.called_at ??
  number.calledAt ??
  undefined;

const getQueueRoutingResult = (number: QueueNumberResponse): QueueRoutingResult | undefined =>
  number.routing_result ?? number.routingResult ?? undefined;

const getQueueNotReachedReason = (number: QueueNumberResponse): QueueNotReachedReason | undefined =>
  number.not_reached_destination_reason ?? number.notReachedDestinationReason ?? undefined;

const getQueueDetectedVoicemail = (number: QueueNumberResponse): boolean | undefined => {
  const value = number.detected_outbound_voicemail ?? number.detectedOutboundVoicemail;
  return value ?? undefined;
};

const getQueueVoicemailLeft = (number: QueueNumberResponse): boolean | undefined => {
  const value = number.voicemail_left ?? number.voicemailLeft;
  return value ?? undefined;
};

/**
 * Returns the flat list of Power Dialer contacts for the queue.
 *
 * Reads `GET /v1/dialing-queues/current` on the remote API (which returns
 * phone numbers only) and runs a parallel `GET /v1/contact?filter[number]=`
 * lookup per number — the same query the call bar uses to resolve an
 * ongoing call — so the side panel / detail columns get the full contact
 * record (name, company, job title, etc.) instead of a bare phone number.
 *
 * Per-call enrichment for already-dialed rows (`/v1/call/{id}`) is fetched
 * lazily in a windowed fashion — see `usePrefetchPowerDialerCalls`.
 */
export const usePowerDialerContacts = (): Result => {
  const { data, isLoading } = $apiAlloRemote.useQuery(
    'get',
    '/v1/dialing-queues/current',
    {},
    { refetchOnWindowFocus: 'always' },
  );

  const queue = (data as { data?: DialingQueueResponse } | undefined)?.data;
  const queueNumbers = useMemo<NormalizedQueueNumber[]>(() => {
    const queueId = queue?.id ?? 'current';
    return (queue?.numbers ?? []).reduce<NormalizedQueueNumber[]>((acc, n, index) => {
      const numberTo = getQueueNumber(n);
      if (!numberTo) {
        return acc;
      }
      const position = n.position ?? index;
      const callId = getQueueCallId(n);
      const callStartDate = getQueueCallStartDate(n);
      const routingResult = getQueueRoutingResult(n);
      const notReachedReason = getQueueNotReachedReason(n);
      const detectedOutboundVoicemail = getQueueDetectedVoicemail(n);
      const voicemailLeft = getQueueVoicemailLeft(n);
      const normalized: NormalizedQueueNumber = {
        queueItemId: `queue-${queueId}-${position}-${numberTo}`,
        numberTo,
        position,
      };
      if (callId) {
        normalized.callId = callId;
      }
      if (callStartDate) {
        normalized.callStartDate = callStartDate;
      }
      if (routingResult) {
        normalized.routingResult = routingResult;
      }
      if (notReachedReason) {
        normalized.notReachedReason = notReachedReason;
      }
      if (detectedOutboundVoicemail !== undefined) {
        normalized.detectedOutboundVoicemail = detectedOutboundVoicemail;
      }
      if (voicemailLeft !== undefined) {
        normalized.voicemailLeft = voicemailLeft;
      }
      acc.push(normalized);
      return acc;
    }, []);
  }, [queue?.id, queue?.numbers]);

  // Parallel contact lookups — one `/v1/contact?filter[number]=E164` per queue
  // number. React Query dedupes/caches automatically, so re-renders and repeat
  // numbers inside the queue don't re-fire requests.
  //
  // Mirrors `caller.tsx` (src/components/dashboard/dialer/caller.tsx): that's
  // the one resolve-by-number path the user can verify against in the app,
  // and it reliably returns named contacts for the same numbers this queue
  // needs to enrich. `filter[number]` is a partial match per the spec, so
  // `page[size]: 5` + client-side exact-E164 filter avoids pulling the wrong
  // contact when multiple numbers share a substring.
  //
  // Dedupe by `numberTo` before handing to `useQueries` — same number at two
  // queue positions would otherwise produce two array entries with identical
  // query keys, which TanStack's QueriesObserver warns about.
  const uniqueNumbers = useMemo(
    () => Array.from(new Set(queueNumbers.map((n) => n.numberTo))),
    [queueNumbers],
  );
  const contactLookups = useQueries({
    queries: uniqueNumbers.map((numberTo) =>
      $apiAllo.queryOptions(
        'get',
        '/v1/contact',
        {
          params: {
            query: {
              'page[size]': 5,
              'filter[number]': numberTo,
              'filter[unnamed]': false,
            },
          },
        },
        { staleTime: 5 * 60 * 1000 },
      ),
    ),
  });

  const contacts = useMemo<SessionContact[]>(() => {
    return queueNumbers.map((n, i) => {
      const fallbackId = `queue-${queue?.id ?? 'current'}-${n.position ?? i}`;
      const lookupIndex = uniqueNumbers.indexOf(n.numberTo);
      const rows = (contactLookups[lookupIndex]?.data as ContactLookupResponse | undefined)?.data;
      // Exact E164 match — `filter[number]` is partial per the API spec, so
      // the first row can belong to a different contact whose number shares
      // a substring. Picks the contact whose `numbers` includes the queue
      // entry verbatim, same as `caller.tsx`.
      const match = rows?.find((r) => r.numbers?.some((num) => num === n.numberTo)) ?? rows?.[0];
      const callId = n.callId;
      const callStartDate = n.callStartDate;
      const dialingState = callId ? ('connected' as const) : ('pending' as const);

      const routing = {
        routing_result: n.routingResult,
        not_reached_destination_reason: n.notReachedReason,
        detected_outbound_voicemail: n.detectedOutboundVoicemail,
        voicemail_left: n.voicemailLeft,
      };

      if (match?.id) {
        // The queue's `number_to` is authoritative for dialing. The contact
        // lookup only contributes display metadata (name, company, etc.) —
        // whatever other numbers the contact has on file are irrelevant to
        // this session and must not leak into the dialable list.
        return {
          ...match,
          queue_item_id: n.queueItemId,
          queue_position: n.position,
          numbers: [n.numberTo],
          dialing_state: dialingState,
          call_id: callId,
          call_start_date: callStartDate,
          ...routing,
        };
      }

      return {
        id: fallbackId,
        queue_item_id: n.queueItemId,
        queue_position: n.position,
        numbers: [n.numberTo],
        emails: [],
        company: null,
        dialing_state: dialingState,
        call_id: callId,
        call_start_date: callStartDate,
        ...routing,
      };
    });
  }, [queueNumbers, uniqueNumbers, contactLookups, queue?.id]);

  return { contacts, isLoading };
};
